/* Site navigation behaviour.
 *
 * Replaces two Webflow inline scripts:
 *
 * 1. The light/dark flip. Webflow used GSAP ScrollTrigger to add `.is-light`
 *    to the nav while any `[data-section="light"]` block is crossing the top of
 *    the viewport (start "top 4%", end "bottom 4%"). Reproduced with an
 *    IntersectionObserver whose root margin collapses the viewport to a 4%-tall
 *    band at the top, which is the same test without the 3 GSAP bundles.
 *
 * 2. The burger, which toggles the mobile panel and swaps the icon.
 *
 * The mega-menu controller and mobile accordion builder (~12KB) are not needed:
 * this nav has no mega panels.
 *
 * No dependencies. Loaded deferred, site-wide.
 */
(function () {
    'use strict';

    var bar = document.getElementById('bg-nav');
    if (!bar) {
        return;
    }

    /* ---- keep clear of Ghost's announcement bar ------------------------- */

    // Ghost injects #announcement-bar-root as a STATIC element at the very top
    // of <body>, client-side, after this script runs. The nav is fixed at
    // top:0, so without this it would sit straight on top of the announcement
    // and hide it completely.
    //
    // Tracking the announcement's bottom edge (rather than just its height)
    // means the nav rides down while the announcement is on screen and slides
    // up to top:0 as it scrolls away - the behaviour a sticky header would
    // give, without giving up `position: fixed`, which the blog hero needs in
    // order to run up behind the bar the way it does on escape.tech.
    var navFixed = document.querySelector('.nav_fixed');

    if (navFixed) {
        var ticking = false;

        var placeBar = function () {
            var ann = document.getElementById('announcement-bar-root');
            var offset = ann ? Math.max(ann.getBoundingClientRect().bottom, 0) : 0;
            navFixed.style.top = offset ? offset + 'px' : '';
        };

        var onScroll = function () {
            if (ticking) {
                return;
            }
            ticking = true;
            requestAnimationFrame(function () {
                ticking = false;
                placeBar();
            });
        };

        var watchAnnouncement = function (ann) {
            placeBar();
            window.addEventListener('scroll', onScroll, {passive: true});
            window.addEventListener('resize', onScroll);

            // Ghost creates the root element EMPTY and fills it once the
            // members API responds, so a single measurement taken at script
            // time reads a height of 0. Re-measure whenever it actually
            // changes size.
            if (window.ResizeObserver) {
                new ResizeObserver(placeBar).observe(ann);
            } else {
                window.addEventListener('load', placeBar);
            }
        };

        var existing = document.getElementById('announcement-bar-root');

        if (existing) {
            watchAnnouncement(existing);
        } else if (window.MutationObserver) {
            // Injected asynchronously; wait for it, then stop watching.
            var bodyWatcher = new MutationObserver(function () {
                var ann = document.getElementById('announcement-bar-root');
                if (ann) {
                    bodyWatcher.disconnect();
                    watchAnnouncement(ann);
                }
            });
            bodyWatcher.observe(document.body, {childList: true});
        }
    }

    /* ---- light/dark flip ------------------------------------------------ */

    // Only the blog feed starts dark; every other template is served with
    // .is-light already on, and has no [data-section="light"] to observe.
    var lightSections = document.querySelectorAll('[data-section="light"]');

    if (lightSections.length && 'IntersectionObserver' in window) {
        var panel = document.getElementById('nav-mobile');
        var active = 0;

        var apply = function () {
            var on = active > 0;
            bar.classList.toggle('is-light', on);
            if (panel) {
                panel.classList.toggle('is-light', on);
            }
        };

        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                // isIntersecting flips as the section enters/leaves the 4% band
                active += entry.isIntersecting ? 1 : -1;
                if (active < 0) {
                    active = 0;
                }
            });
            apply();
        }, {
            // Collapse the viewport to a thin strip at the top: a section
            // "intersects" only while it is crossing the nav.
            rootMargin: '0px 0px -96% 0px',
            threshold: 0
        });

        lightSections.forEach(function (section) {
            observer.observe(section);
        });
    }

    /* ---- burger --------------------------------------------------------- */

    var burger = document.querySelector('.nav_burger-icn');
    var mobile = document.getElementById('nav-mobile');

    if (!burger || !mobile) {
        return;
    }

    // Added on the next frame so the panel does not animate up from -100% on
    // first paint. Webflow does the same with its `meganav-anim` class.
    requestAnimationFrame(function () {
        requestAnimationFrame(function () {
            mobile.classList.add('is-ready');
        });
    });

    var scrollY = 0;

    // The panel is fixed to the top of the viewport, so it has to be pushed
    // down past the bar or the first link hides behind it. Measured rather
    // than hardcoded because the bar's height changes with the breakpoint and
    // because Ghost's announcement bar, when enabled, sits above it.
    var place = function () {
        var top = Math.round(document.querySelector('.nav_fixed').getBoundingClientRect().bottom);
        mobile.style.top = Math.max(top, 0) + 'px';
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('orientationchange', place);
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', place);
    }

    var setOpen = function (open) {
        mobile.classList.toggle('is-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        burger.querySelectorAll('.icn').forEach(function (icon) {
            icon.classList.toggle('is-closed', (icon.dataset.menu === 'open') === open);
        });

        // Lock the page behind the panel without losing the scroll position.
        if (open) {
            scrollY = window.scrollY || 0;
            document.body.style.position = 'fixed';
            document.body.style.top = -scrollY + 'px';
            document.body.style.width = '100%';
        } else if (document.body.style.position === 'fixed') {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
        }
    };

    burger.addEventListener('click', function () {
        place();
        setOpen(!mobile.classList.contains('is-open'));
    });

    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && mobile.classList.contains('is-open')) {
            setOpen(false);
            burger.focus();
        }
    });

    // The burger is swapped in by a container query, so a resize can hide it
    // while the panel is still open — which would leave the body locked.
    window.addEventListener('resize', function () {
        if (mobile.classList.contains('is-open') && getComputedStyle(burger).display === 'none') {
            setOpen(false);
        }
    });
}());
